// =====================================================================
// tpCustomer — shared UI components + helpers
//
// Auth boundary: AuthContext is defined here so any component can call
// useAuth(). The real implementation lives in auth.jsx (RealAuthProvider
// + LoginPage). AuthProvider below is kept as a pass-through fallback so
// the symbol still exists if anything imports it; the live provider is
// RealAuthProvider, wired up in index.html.
// =====================================================================

const { useState, useEffect, useMemo, useRef, useCallback, createContext, useContext } = React;

// ---- Auth boundary --------------------------------------------------
const AuthContext = createContext({
  user: null,
  signIn: () => {},
  signOut: () => {},
});
function AuthProvider({ children }) { return children; }
function useAuth() { return useContext(AuthContext); }

// ---- Persona context (drives dashboard mix) ------------------------
const PersonaContext = createContext({ persona: "GTM", setPersona: () => {} });

// ---- Tweaks context (mode + theme) --------------------------------
const TweaksContext = createContext({ tweaks: {}, setTweak: () => {} });

// ---- Helpers ------------------------------------------------------
// D is a Proxy that reads window.TPCRM_DATA lazily at access time.
// This ensures all D.USERS, D.ACCOUNTS, etc. references in page files
// resolve correctly after DataProvider has fetched and populated the data,
// regardless of when each script was first evaluated by Babel.
const D = new Proxy({}, { get: function (_, key) { return window.TPCRM_DATA && window.TPCRM_DATA[key]; } });

// App-wide default currency, set in Admin → Customisation.
function appCurrency() {
  return (window.TPCRM_DATA && window.TPCRM_DATA.SETTINGS && window.TPCRM_DATA.SETTINGS.defaultCurrency) || "GBP";
}
function fmtMoney(n, ccy) {
  if (n == null) return "—";
  ccy = ccy || appCurrency();
  const map = { USD: "$", EUR: "€", GBP: "£" };
  const sym = map[ccy] || (ccy + " ");
  if (Math.abs(n) >= 1_000_000) return sym + (n / 1_000_000).toFixed(2) + "M";
  if (Math.abs(n) >= 1_000) return sym + (n / 1_000).toFixed(0) + "k";
  return sym + n.toFixed(0);
}
function fmtMoneyExact(n, ccy) {
  if (n == null) return "—";
  ccy = ccy || appCurrency();
  const map = { USD: "$", EUR: "€", GBP: "£" };
  const sym = map[ccy] || (ccy + " ");
  return sym + n.toLocaleString();
}
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function fmtDateShort(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
function daysBetween(a, b) {
  return Math.round((new Date(a) - new Date(b)) / 86400000);
}
function daysAgo(d) {
  if (!d) return null;
  return Math.max(0, Math.round((Date.now() - new Date(d)) / 86400000));
}
function getUser(id) { return D.USERS.find((u) => u.id === id); }
// Sales reps assignable to new work. Excludes disabled reps (soft-retired
// in Admin → Sales reps) so they drop out of owner / interviewer / SE
// pickers and rep rollups, while getUser() above still resolves them for
// historical records. Pass a role string or array to narrow by sales role.
// `includeId` keeps one specific rep in the list even when disabled/off-role
// — edit dialogs pass the record's current owner so it stays selected.
function assignableReps(roles, includeId) {
  const set = roles ? new Set(Array.isArray(roles) ? roles : [roles]) : null;
  return (D.USERS || []).filter((u) => {
    if (includeId && u.id === includeId) return true;
    if (u.disabled) return false;
    return set ? set.has(u.role) : true;
  });
}
function getAccount(id) { return D.ACCOUNTS.find((a) => a.id === id); }
function getContact(id) { return D.CONTACTS.find((c) => c.id === id); }
function getOpp(id) { return D.OPPORTUNITIES.find((o) => o.id === id); }

// ── Opportunity ↔ Contact junction helpers ─────────────────────────────
// Backed by D.OPPORTUNITY_CONTACTS (rows: { oppId, contactId, role,
// roleConfidence, isPrimary }). A contact can hold different roles on
// different deals; on a single deal we store one row per (oppId, contactId).

function getContactsForOpp(oppId) {
  if (!oppId) return [];
  return (D.OPPORTUNITY_CONTACTS || []).filter((oc) => oc.oppId === oppId);
}
function getOppsForContact(contactId) {
  if (!contactId) return [];
  return (D.OPPORTUNITY_CONTACTS || []).filter((oc) => oc.contactId === contactId);
}
function getOppContactRole(oppId, contactId) {
  return (D.OPPORTUNITY_CONTACTS || []).find((oc) => oc.oppId === oppId && oc.contactId === contactId) || null;
}
// First opportunity with the given role on a deal (case-sensitive match).
// Used to derive Champion / Economic Buyer / Primary Contact slots that
// used to live as fixed columns on opportunities.
function getOppContactByRole(oppId, role) {
  if (!oppId || !role) return null;
  const oc = (D.OPPORTUNITY_CONTACTS || []).find((x) => x.oppId === oppId && x.role === role);
  return oc ? getContact(oc.contactId) : null;
}
function getOppPrimaryContact(oppId) {
  const oc = (D.OPPORTUNITY_CONTACTS || []).find((x) => x.oppId === oppId && x.isPrimary);
  return oc ? getContact(oc.contactId) : null;
}
// Contact's "current" opportunity for option-B contact-list display: the
// most-recently-touched open deal. Returns null if the contact has no open
// opp — callers should fall back to showing an em dash rather than guessing.
function getActiveOppForContact(contactId) {
  const rows = getOppsForContact(contactId);
  if (!rows.length) return null;
  const openOpps = rows
    .map((oc) => getOpp(oc.oppId))
    .filter((o) => o && o.stage && !o.stage.startsWith("Closed"));
  if (!openOpps.length) return null;
  // Sort by most recent stage entry, then by created. Both are ISO date
  // strings so string-compare is fine.
  openOpps.sort((a, b) => {
    const ax = a.stageEntry || a.created || "";
    const bx = b.stageEntry || b.created || "";
    return bx.localeCompare(ax);
  });
  return openOpps[0];
}
function initials(name = "") {
  return name.split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

// ---- Common UI components -----------------------------------------
function Badge({ children, tone = "slate", dot = false }) {
  return (
    <span className={`badge badge-${tone}${dot ? " badge-dot" : ""}`}>{children}</span>
  );
}
function StageBadge({ stage }) {
  const t = stage?.startsWith("Closed Won") ? "green"
    : stage?.startsWith("Closed Lost") ? "red"
    : stage?.startsWith("Closed") ? "slate"
    : ["Verbal commitment", "Commercial negotiation", "Legal / Security review"].includes(stage) ? "amber"
    : ["Demo / Evaluation", "Proposal sent", "Discovery completed"].includes(stage) ? "blue"
    : "slate";
  return <Badge tone={t}>{stage}</Badge>;
}
function ForecastBadge({ cat }) {
  const t = { "Closed": "green", "Commit": "green", "Best case": "amber", "Pipeline": "slate" }[cat] || "slate";
  return <Badge tone={t} dot>{cat}</Badge>;
}
// Defaults for the customer-health calculation. These mirror the backend
// defaults in tpCustBe/db/queries/settings.js and are used as a fallback
// whenever D.SETTINGS.healthScore is missing or partial (older clients
// loaded before the setting existed). Admins can override the whole shape
// in Admin -> Customisation -> Customer health score.
const DEFAULT_HEALTH_CONFIG = {
  weights: {
    activityRecency:     40,
    stakeholderCoverage: 20,
    opportunityOutcomes: 20,
    championStrength:    10,
    contactEngagement:   10,
  },
  activityRecencyBands: [
    { maxDays: 14, fraction: 1.0  },
    { maxDays: 30, fraction: 0.75 },
    { maxDays: 60, fraction: 0.5  },
    { maxDays: 90, fraction: 0.25 },
  ],
  recentLossDays:    180,
  recentLossPenalty: 10,
  thresholds: { green: 80, amber: 60 },
};

function getHealthConfig() {
  const fromSettings = D.SETTINGS && D.SETTINGS.healthScore;
  if (!fromSettings) return DEFAULT_HEALTH_CONFIG;
  return {
    weights:              { ...DEFAULT_HEALTH_CONFIG.weights,    ...(fromSettings.weights || {}) },
    activityRecencyBands: Array.isArray(fromSettings.activityRecencyBands) && fromSettings.activityRecencyBands.length
      ? fromSettings.activityRecencyBands
      : DEFAULT_HEALTH_CONFIG.activityRecencyBands,
    recentLossDays:    fromSettings.recentLossDays    != null ? Number(fromSettings.recentLossDays)    : DEFAULT_HEALTH_CONFIG.recentLossDays,
    recentLossPenalty: fromSettings.recentLossPenalty != null ? Number(fromSettings.recentLossPenalty) : DEFAULT_HEALTH_CONFIG.recentLossPenalty,
    thresholds:        { ...DEFAULT_HEALTH_CONFIG.thresholds, ...(fromSettings.thresholds || {}) },
  };
}

function HealthDot({ score }) {
  if (score == null) return <span className="muted">—</span>;
  const t = getHealthConfig().thresholds;
  const tone = score >= t.green ? "green" : score >= t.amber ? "amber" : "red";
  return <Badge tone={tone} dot>{score}</Badge>;
}

// Customer health is derived from real signal (activities, opportunities,
// research, contact engagement), not stored. Returns { score, reason,
// components } — score is null when the account isn't a customer or has
// no signal at all. Weights are normalised against whichever components
// have data, so a customer with no recent research isn't punished for
// missing data they never collected. Weights, activity-recency bands,
// recent-loss penalty and colour thresholds are all admin-configurable
// (see getHealthConfig).
function computeAccountHealth(account) {
  if (!account || account.type !== "Customer") {
    return { score: null, reason: "Not yet a customer", components: [] };
  }
  const accountId = account.id;
  const activities = (D.ACTIVITIES || []).filter(a => a.accountId === accountId);
  const opps       = (D.OPPORTUNITIES || []).filter(o => o.accountId === accountId);
  const surveys    = (D.SURVEYS || []).filter(s => s.accountId === accountId);
  const interviews = (D.INTERVIEWS || []).filter(i => i.accountId === accountId);
  const contacts   = (D.CONTACTS || []).filter(c => c.accountId === accountId);

  if (!activities.length && !opps.length && !surveys.length && !interviews.length && !contacts.length) {
    return { score: null, reason: "No signal data yet", components: [] };
  }

  const cfg = getHealthConfig();
  const W   = cfg.weights;
  const components = [];
  let total = 0, maxTotal = 0;

  // Activity recency
  const actDates = activities.map(a => new Date(a.date).getTime()).filter(t => !isNaN(t));
  if (actDates.length) {
    const days = Math.max(0, Math.round((Date.now() - Math.max(...actDates)) / 86400000));
    const band = cfg.activityRecencyBands.find(b => days <= b.maxDays);
    const pts  = Math.round(W.activityRecency * (band ? band.fraction : 0));
    components.push({ label: "Activity recency", detail: `Last activity ${days}d ago`, points: pts, max: W.activityRecency });
    total += pts; maxTotal += W.activityRecency;
  } else if (opps.length || surveys.length || interviews.length) {
    components.push({ label: "Activity recency", detail: "No activity logged", points: 0, max: W.activityRecency });
    maxTotal += W.activityRecency;
  }

  // Stakeholder coverage on most recent opp — half weight for champion,
  // half for multi-threaded.
  if (opps.length) {
    const recent = opps.slice().sort((a, b) =>
      (b.actualClose || b.closeDate || b.stageEntry || "").localeCompare(a.actualClose || a.closeDate || a.stageEntry || "")
    )[0];
    // Champion presence is now derived from the opportunity_contacts junction
    // (role = 'Champion' on this deal), not a column on the opp row.
    const hasChampion = !!getOppContactByRole(recent.id, "Champion");
    const half = W.stakeholderCoverage / 2;
    let pts = 0;
    if (hasChampion)         pts += half;
    if (recent.multiThreaded) pts += half;
    pts = Math.round(pts);
    const detail = [
      hasChampion ? "champion identified" : "no champion",
      recent.multiThreaded ? "multi-threaded" : "single-threaded",
    ].join(" · ");
    components.push({ label: "Stakeholder coverage", detail, points: pts, max: W.stakeholderCoverage });
    total += pts; maxTotal += W.stakeholderCoverage;
  }

  // Win/loss outcomes
  const won  = Number(account.wonOpps)  || 0;
  const lost = Number(account.lostOpps) || 0;
  if (won + lost > 0 || opps.length) {
    const ratio = (won + lost) > 0 ? won / (won + lost) : 0.5;
    let pts = Math.round(ratio * W.opportunityOutcomes);
    const recentLoss = opps.some(o => {
      if (o.stage !== "Closed Lost" || !o.actualClose) return false;
      const d = Math.round((Date.now() - new Date(o.actualClose).getTime()) / 86400000);
      return !isNaN(d) && d >= 0 && d <= cfg.recentLossDays;
    });
    if (recentLoss) pts = Math.max(0, pts - cfg.recentLossPenalty);
    const detail = `${won} won · ${lost} lost${recentLoss ? " · recent loss" : ""}`;
    components.push({ label: "Opportunity outcomes", detail, points: pts, max: W.opportunityOutcomes });
    total += pts; maxTotal += W.opportunityOutcomes;
  }

  // Champion strength from research
  const research = [...surveys, ...interviews];
  if (research.length) {
    const strong = research.filter(r => r.championStrength === "Strong").length;
    const weak   = research.filter(r => r.championStrength === "Weak").length;
    const ratio = (strong + weak) > 0 ? strong / (strong + weak) : 0.5;
    const pts = Math.round(ratio * W.championStrength);
    const detail = `${strong} strong · ${weak} weak (${research.length} research)`;
    components.push({ label: "Champion strength", detail, points: pts, max: W.championStrength });
    total += pts; maxTotal += W.championStrength;
  }

  // Contact engagement
  if (contacts.length) {
    const optOuts  = contacts.filter(c => c.optOut).length;
    const fatigued = contacts.filter(c => c.fatigue).length;
    const healthy  = Math.max(0, contacts.length - optOuts - fatigued);
    const ratio = healthy / contacts.length;
    const pts = Math.round(ratio * W.contactEngagement);
    const detail = `${optOuts} opt-out · ${fatigued} fatigued · ${healthy} engaged`;
    components.push({ label: "Contact engagement", detail, points: pts, max: W.contactEngagement });
    total += pts; maxTotal += W.contactEngagement;
  }

  const score = maxTotal > 0 ? Math.round((total / maxTotal) * 100) : null;
  return { score, reason: null, components };
}
function Avatar({ user, size = 24, tone = null }) {
  if (!user) return null;
  const u = typeof user === "string" ? getUser(user) : user;
  if (!u) return <span className="avatar">?</span>;
  const cls = tone ? `avatar avatar-${tone}` : "avatar";
  return (
    <span className={cls} style={{ width: size, height: size, fontSize: size * 0.42 }}>
      {initials(u.name)}
    </span>
  );
}
function Donut({ value, max = 100, size = 36, color }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="donut" style={{ "--p": pct, "--c": color || "var(--accent)", width: size, height: size }}>
      <span>{Math.round(value)}</span>
    </div>
  );
}
function Stat({ label, value, delta, deltaDir }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value numeric">{value}</div>
      {delta && (
        <div className={`stat-delta ${deltaDir === "up" ? "delta-up" : deltaDir === "down" ? "delta-down" : ""}`}>
          {deltaDir === "up" ? "▲" : deltaDir === "down" ? "▼" : ""} {delta}
        </div>
      )}
    </div>
  );
}
function BarRow({ label, value, max, tone = "" }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className="bar-row">
      <div>{label}</div>
      <div className="bar-track"><div className={`bar-fill ${tone}`} style={{ width: `${pct}%` }} /></div>
      <div className="bar-num">{value}</div>
    </div>
  );
}
// MEDDPICCR derivation lives in pages/opportunity-meddpiccr.js so the
// rules can be unit-tested under node. Both MeddPip and meddScore go
// through the helper — never read opp.meddpicc directly — so adding an
// Economic Buyer stakeholder or filling competitors flips the score on
// the next render with no manual click (P1.1 acceptance criterion).
function deriveOppState(opp) {
  const stakeholders = (D.OPPORTUNITY_CONTACTS || []).filter(oc => oc.oppId === opp.id);
  const risks = (D.OPPORTUNITY_RISKS || []).filter(r => r.oppId === opp.id);
  return window.tpMeddpiccr.deriveMeddpiccr(opp, stakeholders, D.SETTINGS, risks);
}
function MeddPip({ opp }) {
  const derived = deriveOppState(opp);
  return (
    <span className="medd-pip">
      {D.MEDDPICCR_KEYS.map(k => {
        const entry = derived[k.key];
        const on = !!(entry && entry.value);
        const overridden = entry && entry.source && entry.source !== "derived";
        const title = k.label + (overridden ? ` (override: ${entry.reason || ""})` : "");
        return (
          <span
            key={k.key}
            className={`medd-pip-dot${on ? " on" : ""}${overridden ? " override" : ""}`}
            title={title}
          />
        );
      })}
    </span>
  );
}
function meddScore(opp) {
  const derived = deriveOppState(opp);
  return window.tpMeddpiccr.meddScoreFromDerived(derived, D.MEDDPICCR_KEYS.map(k => k.key));
}
// Real sequence performance, computed from enrollment records.
function seqStats(sequenceId) {
  const rows = (D.ENROLLMENTS || []).filter(e => e.sequenceId === sequenceId);
  const enrolled = rows.length;
  const replied  = rows.filter(e => e.status === "Replied" || e.status === "Meeting booked").length;
  const meetings = rows.filter(e => e.status === "Meeting booked").length;
  return {
    enrolled, replied, meetings,
    replyRate:   enrolled ? replied / enrolled : 0,
    meetingRate: enrolled ? meetings / enrolled : 0,
  };
}
function ResearchBadge({ surveys, interviews }) {
  if (!surveys && !interviews) return null;
  return (
    <span className="badge badge-amber" title="Research touchpoints">
      <span style={{ fontSize: 10 }}>◆</span>
      {surveys}s · {interviews}i
    </span>
  );
}

// ---- Tabs ----------------------------------------------------------
function Tabs({ tabs, current, onChange }) {
  return (
    <div className="tabs">
      {tabs.map(t => (
        <div key={t.key}
             className={`tab${current === t.key ? " active" : ""}`}
             onClick={() => onChange(t.key)}>
          {t.label}
          {t.count != null && <span className="tab-count">{t.count}</span>}
        </div>
      ))}
    </div>
  );
}

// ---- Quick action dialog -----------------------------------------
const QUICK_ACTION_META = {
  email:    { title: "Log email",       cta: "Log" },
  call:     { title: "Log call",        cta: "Log" },
  meeting:  { title: "Schedule meeting",cta: "Schedule" },
  task:     { title: "Create task",     cta: "Create" },
  activity: { title: "New activity",    cta: "Log" },
};
function QuickActionDialog({ type, link, onClose, onSave }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ date: today, subject: "", notes: "",
    direction: type === "call" ? "Outbound" : "Sent", duration: "", outcome: "Connected",
    priority: "Medium", activityType: "Email", time: "", title: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  const meta = QUICK_ACTION_META[type];

  // Translate the per-type form into a unified activity record. The optional
  // `link` ties the activity to the contact / account / opportunity it was
  // logged from so it surfaces on that record's Activity tab.
  function buildActivity() {
    const l = link || {};
    const base = {
      id: "act_" + Math.random().toString(36).slice(2, 10),
      date: form.date,
      body: form.notes.trim() || null,
      contactId: l.contactId || null,
      accountId: l.accountId || null,
      oppId: l.oppId || null,
    };
    if (type === "email")
      return { ...base, type: "Email", direction: form.direction,
               subject: form.subject.trim() || "Email" };
    if (type === "call")
      return { ...base, type: "Call", direction: form.direction, outcome: form.outcome,
               duration: form.duration ? Number(form.duration) : null,
               subject: "Call · " + form.outcome };
    if (type === "meeting")
      return { ...base, type: "Meeting", subject: form.title.trim() || "Meeting",
               duration: form.duration ? Number(form.duration) : null };
    if (type === "task")
      return { ...base, type: "Task", subject: form.title.trim() || "Task",
               priority: form.priority };
    return { ...base, type: form.activityType, subject: form.activityType };
  }

  async function handleSave() {
    setSaving(true); setError(null);
    try {
      await tpAPI.activities.create(buildActivity());
      if (onSave) onSave();
      onClose();
    } catch (e) {
      setError(e.message || "Failed to save.");
      setSaving(false);
    }
  }

  return (
    <Dialog title={meta.title} onClose={onClose} footer={
      <>
        <button className="btn btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : meta.cta}</button>
      </>
    }>
      {type === "email" && <>
        <div className="form-row">
          <div className="form-field"><label>Direction</label>
            <select value={form.direction} onChange={e => set("direction", e.target.value)}>
              <option>Sent</option><option>Received</option>
            </select>
          </div>
          <div className="form-field"><label>Date</label>
            <input type="date" value={form.date} onChange={e => set("date", e.target.value)} />
          </div>
        </div>
        <div className="form-field"><label>Subject</label>
          <input value={form.subject} onChange={e => set("subject", e.target.value)} placeholder="Email subject" autoFocus />
        </div>
        <div className="form-field"><label>Notes</label>
          <textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Brief excerpt or outcome…" />
        </div>
      </>}
      {type === "call" && <>
        <div className="form-row">
          <div className="form-field"><label>Direction</label>
            <select value={form.direction} onChange={e => set("direction", e.target.value)}>
              <option>Outbound</option><option>Inbound</option>
            </select>
          </div>
          <div className="form-field"><label>Date</label>
            <input type="date" value={form.date} onChange={e => set("date", e.target.value)} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field"><label>Outcome</label>
            <select value={form.outcome} onChange={e => set("outcome", e.target.value)}>
              <option>Connected</option><option>Voicemail</option><option>No answer</option>
            </select>
          </div>
          <div className="form-field"><label>Duration (min)</label>
            <input type="number" min="1" value={form.duration} onChange={e => set("duration", e.target.value)} placeholder="15" autoFocus />
          </div>
        </div>
        <div className="form-field"><label>Notes</label>
          <textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Call summary…" />
        </div>
      </>}
      {type === "meeting" && <>
        <div className="form-field"><label>Title</label>
          <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Discovery call" autoFocus />
        </div>
        <div className="form-row">
          <div className="form-field"><label>Date</label>
            <input type="date" value={form.date} onChange={e => set("date", e.target.value)} />
          </div>
          <div className="form-field"><label>Time</label>
            <input type="time" value={form.time} onChange={e => set("time", e.target.value)} />
          </div>
        </div>
        <div className="form-field"><label>Duration (min)</label>
          <select value={form.duration} onChange={e => set("duration", e.target.value)}>
            <option value="30">30 min</option><option value="60">60 min</option>
            <option value="90">90 min</option><option value="120">2 hours</option>
          </select>
        </div>
        <div className="form-field"><label>Notes</label>
          <textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Agenda or context…" />
        </div>
      </>}
      {type === "task" && <>
        <div className="form-field"><label>Task</label>
          <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Follow up on proposal" autoFocus />
        </div>
        <div className="form-row">
          <div className="form-field"><label>Due date</label>
            <input type="date" value={form.date} onChange={e => set("date", e.target.value)} />
          </div>
          <div className="form-field"><label>Priority</label>
            <select value={form.priority} onChange={e => set("priority", e.target.value)}>
              <option>High</option><option>Medium</option><option>Low</option>
            </select>
          </div>
        </div>
        <div className="form-field"><label>Notes</label>
          <textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Additional context…" />
        </div>
      </>}
      {type === "activity" && <>
        <div className="form-row">
          <div className="form-field"><label>Type</label>
            <select value={form.activityType} onChange={e => set("activityType", e.target.value)} autoFocus>
              <option>Email</option><option>Call</option><option>Meeting</option>
              <option>Note</option><option>LinkedIn</option><option>Other</option>
            </select>
          </div>
          <div className="form-field"><label>Date</label>
            <input type="date" value={form.date} onChange={e => set("date", e.target.value)} />
          </div>
        </div>
        <div className="form-field"><label>Notes</label>
          <textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="What happened…" />
        </div>
      </>}
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

// ---- Right rail (audit + quick actions) ---------------------------
function RightRail({ record, kind = "record", link }) {
  const [action, setAction] = useState(null);
  const [saved, setSaved] = useState(null);
  function handleSave() {
    const label = QUICK_ACTION_META[action]?.title;
    setAction(null);
    setSaved(label);
    setTimeout(() => setSaved(null), 2500);
  }
  return (
    <aside className="rail">
      <div className="card">
        <div className="card-head"><div className="card-title">Quick actions</div></div>
        <div className="card-body flex-col gap-8">
          {saved && <div className="quick-saved">✓ {saved} logged</div>}
          <button className="btn" onClick={() => setAction("email")}><span>✉</span> Log email</button>
          <button className="btn" onClick={() => setAction("call")}><span>☎</span> Log call</button>
          <button className="btn" onClick={() => setAction("meeting")}><span>📅</span> Schedule meeting</button>
          <button className="btn" onClick={() => setAction("task")}><span>✓</span> Create task</button>
          <button className="btn btn-accent" onClick={() => setAction("activity")}><span>+</span> New activity</button>
        </div>
      </div>
      {action && <QuickActionDialog type={action} link={link} onClose={() => setAction(null)} onSave={handleSave} />}
      <div className="card">
        <div className="card-head"><div className="card-title">Record audit</div></div>
        <div className="card-body">
          <div className="kv">
            <div className="kv-label">Created by</div>
            <div className="kv-value">{getUser(record?.createdBy || record?.owner)?.name || "—"}</div>
            <div className="kv-label">Created</div>
            <div className="kv-value">{fmtDate(record?.created || "2025-01-01")}</div>
            <div className="kv-label">Last modified</div>
            <div className="kv-value">{fmtDate(record?.lastActivity || record?.meddpiccUpdated || "2026-04-25")}</div>
            <div className="kv-label">Data source</div>
            <div className="kv-value">{record?.dataSource || "manual"}</div>
            <div className="kv-label">Duplicate</div>
            <div className="kv-value">No</div>
            <div className="kv-label">Record ID</div>
            <div className="kv-value-mono">{record?.id || "—"}</div>
          </div>
        </div>
      </div>
    </aside>
  );
}

// ---- Activity timeline (shared: opp / account / contact detail) ---
// Renders the body of an activity card from real activity records.
// `activities` is expected pre-filtered and newest-first.
function ActivityTimeline({ activities, empty }) {
  const rows = activities || [];
  if (!rows.length) {
    return <div className="card-body muted">{empty || "No activity recorded yet."}</div>;
  }
  return (
    <div className="card-body">
      <div className="timeline">
        {rows.map(act => {
          const tone = act.type === "Meeting" ? " tr-meeting"
            : act.outcome === "Positive" ? " tr-positive" : "";
          const meta = [act.type, act.direction, act.outcome].filter(Boolean).join(" · ");
          return (
            <div key={act.id} className={`timeline-row${tone}`}>
              <div className="timeline-meta">
                <span>{meta}</span>
                <span>{fmtDate(act.date)}</span>
              </div>
              <div className="timeline-title">{act.subject}</div>
              {act.body && <div className="timeline-body">{act.body}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Filter toolbar (lightweight) --------------------------------
function FilterToolbar({ children, count, total }) {
  return (
    <div className="tbl-toolbar">
      {children}
      <div className="tbl-meta">
        {count != null ? `${count}${total != null ? ` of ${total}` : ""} records` : null}
      </div>
    </div>
  );
}

// ---- Page Head ----------------------------------------------------
function PageHead({ title, sub, actions }) {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">{title}</h1>
        {sub && <p className="page-sub">{sub}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

// ---- New opportunity dialog (shared: accounts + opportunities) ----
function NewOpportunityDialog({ accountId, onClose, navigate }) {
  const today = new Date().toISOString().slice(0, 10);
  const accounts = D.ACCOUNTS || [];
  const users = assignableReps("AE");
  const stages = D.STAGES || [];
  const forecastCats = D.FORECAST_CATEGORIES || [];
  const motions = ["SME self-serve", "SME assisted", "Mid-market", "Enterprise"];
  const [form, setForm] = useState({
    name: "", accountId: accountId || "", stage: stages[0] || "Prospecting",
    forecastCategory: "Pipeline", motion: "Mid-market",
    acv: "", currency: appCurrency(), owner: users[0]?.id || "", created: today,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  async function handleSave() {
    if (!form.name.trim()) { setError("Deal name is required."); return; }
    if (!form.accountId) { setError("Select an account."); return; }
    setSaving(true); setError(null);
    try {
      const id = "o_" + Math.random().toString(36).slice(2, 9);
      const res = await tpAPI.opportunities.create({ id, ...form, acv: form.acv ? Number(form.acv) : null, meddpicc: {}, daysInStage: 0, history: [], products: [], competitors: [] });
      onClose();
      navigate("sales/opportunities/" + (res.id || id));
    } catch(e) { setError(e.message || "Failed to create opportunity."); setSaving(false); }
  }
  return (
    <Dialog title="New opportunity" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Creating…" : "Create opportunity"}</button>
    </>}>
      <div className="form-field"><label>Deal name *</label>
        <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Acme — Platform expansion" autoFocus />
      </div>
      <div className="form-field"><label>Account *</label>
        <select value={form.accountId} onChange={e => set("accountId", e.target.value)} disabled={!!accountId}>
          <option value="">— Select account —</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      <div className="form-row">
        <div className="form-field"><label>ACV</label>
          <input type="number" value={form.acv} onChange={e => set("acv", e.target.value)} placeholder="100000" />
        </div>
        <div className="form-field"><label>Currency</label>
          <select value={form.currency} onChange={e => set("currency", e.target.value)}>
            <option>USD</option><option>EUR</option><option>GBP</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Stage</label>
          <select value={form.stage} onChange={e => set("stage", e.target.value)}>
            {stages.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-field"><label>Forecast</label>
          <select value={form.forecastCategory} onChange={e => set("forecastCategory", e.target.value)}>
            {forecastCats.map(f => <option key={f}>{f}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Motion</label>
          <select value={form.motion} onChange={e => set("motion", e.target.value)}>
            {motions.map(m => <option key={m}>{m}</option>)}
          </select>
        </div>
        <div className="form-field"><label>Owner</label>
          <select value={form.owner} onChange={e => set("owner", e.target.value)}>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

// ---- Dialog (modal) ----------------------------------------------
// `width` (optional) overrides the CSS default of 520px — pass any
// valid CSS length, e.g. "880px" or "min(960px, 90vw)". The .dialog
// `max-width: calc(100vw - 32px)` still caps it on narrow viewports.
function Dialog({ title, onClose, children, footer, width }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return ReactDOM.createPortal(
    <div className="dialog-backdrop" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dialog" style={width ? { width } : undefined}>
        <div className="dialog-head">
          <div className="dialog-title">{title}</div>
          <button className="dialog-close" onClick={onClose}>✕</button>
        </div>
        <div className="dialog-body">{children}</div>
        {footer && <div className="dialog-foot">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

// expose
Object.assign(window, {
  AuthProvider, AuthContext, useAuth,
  PersonaContext, TweaksContext,
  fmtMoney, fmtMoneyExact, fmtDate, fmtDateShort, daysBetween, daysAgo,
  getUser, getAccount, getContact, getOpp, initials,
  getContactsForOpp, getOppsForContact, getOppContactRole,
  getOppContactByRole, getOppPrimaryContact, getActiveOppForContact,
  Badge, StageBadge, ForecastBadge, HealthDot, computeAccountHealth, Avatar, Donut, Stat, BarRow,
  MeddPip, meddScore, deriveOppState, seqStats, ResearchBadge, Tabs, RightRail, FilterToolbar, PageHead,
  Dialog, QuickActionDialog, NewOpportunityDialog, ActivityTimeline,
});
